import { useActingRole, type ActingRole } from '@brush/auth';
import { atBreakpoint, initialRail, Rundown, toggleRail, type RundownRailControl } from '@brush/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { RoomView } from '../nav';
import { useSession } from '../api';
import { APP_TITLE, roomDocumentTitle } from '../documentTitle';
import { loadRundownHidden, saveRundownHidden } from '../preferences';
import { useCurrentUser, useRoomSummary } from '../queries';
import { useRoom } from '../room';
import { rundownSegments, rundownSpan } from '../rundown';
import { revealScrollTo } from '../reveal';
import { MainRevealProvider, MainScrollProvider } from '../scroll';
import { roleChoicesFor, shortLabelFor } from '../roles';
import { useUnsaved } from '../unsaved';
import { About } from './About';
import { HeaderMenu } from './HeaderMenu';
import { RundownPeriodSummary } from './RundownPeriodSummary';
import { SectionMenu, type SectionEntry } from './SectionMenu';

/**
 * Direction C — "The Rail", gallery side.
 *
 * The room is a producer's desk: a charcoal rail across the top carrying identity and
 * navigation, the rundown pinned down the left (a strip above the content on tablet, one
 * sticky line on a phone), and rows to scan in the middle.
 *
 * The shell is exactly one viewport tall and never scrolls: the rail and the main area are
 * **separate scrolling regions**, so the navigation and the running order stay put while a long
 * list moves under them. That is what the rundown is for — "the one element that never scrolls
 * away" is a promise the page-level scroll it used to sit inside could not keep.
 *
 * Signal orange is reserved for work waiting on the operator, so the active nav item is
 * marked in chalk rather than orange — an underline is not a job to do, and diluting the
 * signal is what makes it stop working. The brand block stays orange as identity, and is
 * byte-identical to player-web's: that is the handshake that keeps the two apps one product.
 *
 * **The right-hand end of the rail has two arrangements, not one that degrades.** From 2xl (1536px) the
 * version line, the role switcher, the room switcher and Sign out sit in the header. Below it
 * every one of them is hidden and `HeaderMenu` carries them instead, behind a single 44px
 * control — the nav is what an operator touches all day, so it is what the space belongs to (periods
 * A6d: beside the cluster the section row did not fit below about 1900px, on main too). The room is named
 * at the top of the Rundown rail (and the tablet strip), never beside the row from `desktop:` up. Anything added to that cluster needs adding to both
 * arrangements; the handlers are defined once here and passed to both so the unsaved-work guard
 * and the cache clear cannot come adrift between the sizes.
 */

/** The width from which the Rundown is a column beside the content (Tailwind's `lg`); below it, an overlay. */
const LARGE = '(min-width: 64rem)';
const isLarge = () => window.matchMedia(LARGE).matches;
/** The rail's running order, which every Rundown switch names in `aria-controls`. */
const RUNNING_ORDER_ID = 'rundown-running-order';

interface NavEntry {
  view: RoomView;
  label: string;
  enabled: boolean;
}

export function RoomShell({
  view,
  onNavigate,
  onChangePeriods,
  counts,
  children,
}: {
  view: RoomView;
  onNavigate: (view: RoomView) => void;
  /** Opens Settings → Preferences → Periods, where the Rundown period is chosen (UP21, A6). */
  onChangePeriods: () => void;
  counts: Partial<Record<RoomView, number>>;
  children: ReactNode;
}) {
  const { room, rooms, roomId, setRoomId } = useRoom();
  const me = useCurrentUser();
  const { actingRole, choose } = useActingRole();
  const queryClient = useQueryClient();

  // Offered only when there is more than one to switch between — a control with a single option
  // is furniture. The list is the server's answer about this account, so a withdrawn assignment
  // removes the option rather than leaving one that is refused when used.
  const roleChoices = roleChoicesFor(me.data?.availableRoles ?? []);

  const { displayName, signOut } = useSession();
  const { leave } = useUnsaved();
  // The rundown comes cut and ordered from the room summary — the same cached answer App's badges read
  // — at the room's own gaming day, not from the whole tournament list cut in the browser.
  const summary = useRoomSummary(roomId);

  const switchRole = (next: ActingRole) => {
    // A way out of an editor, exactly as the room switcher and the section nav are — and a
    // costlier one, since what comes back is read by somebody else.
    leave(() => {
      choose(next);
      queryClient.clear();
    });
  };

  const data = summary.data;
  const segments = useMemo(
    () => (data ? rundownSegments(data, () => onNavigate('tournaments')) : []),
    [data, onNavigate],
  );
  const span = useMemo(() => (data ? rundownSpan(data) : ''), [data]);

  // #575: the rail hides to a handle from lg, remembered per browser, and opens as an overlay below lg, always starting
  // closed. Only what is drawn changes: the summary above is read whatever the rail shows, so hiding it delays nothing.
  const [rail, setRail] = useState(() => initialRail(loadRundownHidden()));
  // Set by the switch and by Esc, so focus moves to whichever switch is left on screen; a click elsewhere keeps its own.
  const refocus = useRef(false);
  const onToggleRail = useCallback(() => {
    refocus.current = true;
    setRail((s) => toggleRail(s, isLarge()));
  }, []);
  const railControl = useMemo<RundownRailControl>(
    () => ({ state: rail, onToggle: onToggleRail, contentId: RUNNING_ORDER_ID }),
    [rail, onToggleRail],
  );

  useEffect(() => {
    saveRundownHidden(rail.collapsed);
  }, [rail.collapsed]);

  // The tab names the room, so where you are is said with the rail hidden too; the handle deliberately does not.
  useEffect(() => {
    document.title = roomDocumentTitle(room?.name);
    return () => {
      document.title = APP_TITLE;
    };
  }, [room?.name]);

  // An overlay left open as the window widens past lg would sit over the column it became.
  useEffect(() => {
    const query = window.matchMedia(LARGE);
    const onChange = () => setRail((s) => atBreakpoint(s, query.matches));
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  // The overlay closes on Esc and on a press anywhere outside it but a switch (which toggles it) or the backdrop (which
  // closes it on its own click, so that click cannot land on whatever was under it).
  useEffect(() => {
    if (!rail.overlayOpen) return;
    const close = () => setRail((s) => ({ ...s, overlayOpen: false }));
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      refocus.current = true;
      close();
    };
    const onDown = (e: PointerEvent) => {
      if (!(e.target instanceof Element)) return;
      if (e.target.closest('[data-rundown-rail], [data-rundown-switch], [data-rundown-backdrop]')) return;
      close();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
    };
  }, [rail.overlayOpen]);

  useEffect(() => {
    if (!refocus.current) return;
    refocus.current = false;
    const shown = (kind: string) =>
      [...document.querySelectorAll<HTMLElement>(`[data-rundown-switch="${kind}"]`)].find((el) => el.getClientRects().length > 0);
    const large = isLarge();
    (rail.overlayOpen || (large && !rail.collapsed) ? shown('rail') : shown(large ? 'handle' : 'bar'))?.focus();
  }, [rail]);

  const mainRef = useRef<HTMLElement>(null);
  // Stable, so a view can list it in an effect's dependencies without refiring every render.
  const scrollMainToTop = useCallback(() => {
    mainRef.current?.scrollTo({ top: 0 });
  }, []);
  // A Change link's reveal of the Periods card moves <main>, the page's one scroller, and nothing else (A6c).
  const revealInMain = useCallback((element: HTMLElement, reducedMotion: boolean) => {
    const main = mainRef.current;
    if (!main) return;
    main.scrollTo(
      revealScrollTo({
        scrollerTop: main.getBoundingClientRect().top,
        scrollTop: main.scrollTop,
        targetTop: element.getBoundingClientRect().top,
        reducedMotion,
      }),
    );
  }, []);

  // A section is a new screen, and a new screen starts at the top rather than under whatever
  // scroll position the last one left behind.
  useEffect(() => {
    scrollMainToTop();
  }, [view, scrollMainToTop]);

  if (!room) return null;

  const entries: NavEntry[] = [
    { view: 'overview', label: 'Overview', enabled: true },
    { view: 'tournaments', label: 'Tournaments', enabled: true },
    { view: 'leaderboards', label: 'Leaderboards', enabled: room.canLeaderboard },
    { view: 'fantasy', label: 'Fantasy', enabled: room.canFantasy },
    { view: 'ratings', label: 'Ratings', enabled: room.canRatings },
    { view: 'players', label: 'Players', enabled: true },
    // Templates and points systems are panes of Settings, not sections: see `nav.ts`.
    { view: 'settings', label: 'Settings', enabled: true },
  ];

  // The phone's arrangement reads the same list with the counts folded in, so a section cannot
  // appear in one arrangement and not the other, or carry a different count in each.
  const sections: SectionEntry[] = entries.map((entry) => ({
    ...entry,
    count: counts[entry.view],
  }));

  // `h-dvh` rather than `h-screen`: on a phone `100vh` is the viewport measured with the browser
  // chrome retracted, so the bottom of a shell that is exactly one screen tall sits under the
  // address bar — unreachable, now that the page itself no longer scrolls.
  return (
    // `relative` here and on <main> is what keeps the page to ONE scroller (A6c): an absolutely positioned element with
    // no positioned ancestor — a visually hidden `sr-only` label — takes the initial containing block, escapes <main>'s
    // overflow and stretches the window into a second scroller beside it.
    <div className="relative flex h-dvh flex-col overflow-hidden bg-ground print:h-auto print:overflow-visible">
      <header className="flex h-14 shrink-0 items-stretch bg-rail-900 text-white print:hidden">
        <span
          className="flex shrink-0 items-center bg-signal pl-[22px] pr-[26px] text-base font-bold italic tracking-[0.06em] text-white"
          style={{ clipPath: 'polygon(0 0, 100% 0, calc(100% - 14px) 100%, 0 100%)' }}
        >
          BRUSH
        </span>

        <span className="hidden shrink-0 items-center truncate pl-1.5 pr-4 text-[10.5px] font-semibold uppercase tracking-[0.2em] text-rail-300 sm:flex desktop:hidden">
          {room.name}
        </span>

        {/* Where you are, on a phone. The row below cannot say it — seven entries do not fit
            430px, so it scrolled sideways and showed about one and a half labels. This is the
            same information the active nav item carries, in the space the row used to occupy. */}
        <span className="flex min-w-0 flex-1 items-center truncate pl-1.5 pr-2 text-[13px] font-semibold uppercase tracking-[0.08em] text-white desktop:hidden">
          {entries.find((e) => e.view === view)?.label}
          {typeof counts[view] === 'number' ? (
            <span className="num ml-2 shrink-0 text-[11px] font-bold text-white/40">
              {counts[view]}
            </span>
          ) : null}
        </span>

        <nav
          className="hidden flex-1 items-stretch overflow-x-auto overflow-y-hidden [scrollbar-width:none] desktop:flex"
          aria-label="Room sections"
        >
          {entries.map((entry) =>
            entry.enabled ? (
              <button
                key={entry.view}
                type="button"
                onClick={() => onNavigate(entry.view)}
                aria-current={view === entry.view ? 'page' : undefined}
                className={`flex min-h-[44px] shrink-0 items-center gap-2 whitespace-nowrap px-3 text-[12.5px] font-semibold uppercase tracking-[0.05em] transition ${
                  view === entry.view
                    ? 'bg-white/5 text-white shadow-[inset_0_-4px_0_#ffffff]'
                    : 'text-rail-300 hover:text-white'
                }`}
              >
                {entry.label}
                {typeof counts[entry.view] === 'number' ? (
                  <span className="num text-[11px] font-bold text-white/40">
                    {counts[entry.view]}
                  </span>
                ) : null}
              </button>
            ) : (
              <span
                key={entry.view}
                title="Ask the platform admin to enable this for your room."
                className="flex min-h-[44px] shrink-0 items-center whitespace-nowrap px-3 text-[12.5px] font-semibold uppercase tracking-[0.05em] text-white/20"
              >
                {entry.label}
              </span>
            ),
          )}
        </nav>

        <div className="flex shrink-0 items-center gap-3 pl-3 pr-4">
          <About className="hidden text-rail-300 hover:text-white 2xl:inline" />

          {roleChoices.length > 1 && actingRole ? (
            <select
              value={actingRole}
              onChange={(e) => switchRole(e.target.value as ActingRole)}
              aria-label="Switch role"
              // Beside the room switcher and styled identically: both answer "which slice of the
              // platform am I looking at", and the role is the one that decides what the other
              // can even offer. Chalk rather than signal orange — a role in force is a statement
              // about you, not a job waiting to be done.
              className="hidden max-w-[130px] border border-white/20 bg-transparent px-2 py-1.5 text-[11px] uppercase tracking-[0.06em] text-rail-300 outline-none 2xl:block"
            >
              {roleChoices.map((choice) => (
                <option key={choice.id} value={choice.id} className="text-ink-900">
                  {shortLabelFor(choice.id as ActingRole)}
                </option>
              ))}
            </select>
          ) : null}

          {rooms.length > 1 ? (
            <select
              value={roomId ?? ''}
              // Another room's templates are different records, so this discards a draft as
              // surely as leaving the section does.
              onChange={(e) => leave(() => setRoomId(e.target.value))}
              aria-label="Switch room"
              // The whole name, since with the rail hidden this is where the room is named (walk feedback on #575): a
              // native select is as wide as its longest room, capped so only a very long name is cut, and the name is
              // on hover. 22rem holds every room on Dev and leaves the section row ~118px spare at 1536.
              title={room.name}
              className="hidden max-w-[22rem] truncate border border-white/20 bg-transparent px-2 py-1.5 text-[11px] uppercase tracking-[0.06em] text-rail-300 outline-none 2xl:block"
            >
              {rooms.map((r) => (
                <option key={r.id} value={r.id} className="text-ink-900">
                  {r.name}
                </option>
              ))}
            </select>
          ) : null}

          {signOut ? (
            <>
              {displayName ? (
                <span className="hidden max-w-[160px] truncate text-[11.5px] text-rail-300 2xl:block">
                  {displayName}
                </span>
              ) : null}
              <button
                type="button"
                onClick={signOut}
                className="hidden border border-white/20 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.1em] text-rail-300 transition hover:border-white/50 hover:text-white 2xl:block"
              >
                Sign out
              </button>
            </>
          ) : (
            <span className="hidden text-[10px] font-semibold uppercase tracking-[0.1em] text-white/25 2xl:inline">
              Dev bypass
            </span>
          )}

          {/* Below `md` everything above this line is hidden and arrives in these two instead.
              `SectionMenu` takes the same already-guarded `onNavigate` the row above calls, and
              both switchers take the same routed handlers the desktop ones use — so the
              unsaved-work guard and the cache clear cannot come adrift between the sizes. */}
          <SectionMenu entries={sections} view={view} onNavigate={onNavigate} />

          <HeaderMenu
            room={room}
            rooms={rooms}
            roomId={roomId}
            onSelectRoom={(id) => leave(() => setRoomId(id))}
            roleChoices={roleChoices}
            onSelectRole={switchRole}
            displayName={displayName}
            signOut={signOut}
          />
        </div>
      </header>

      {/* Phone line and tablet strip sit above the content; the rail sits beside it. */}
      {/* The period in force is stated in every arrangement, the phone's one line included (known gap 148): the
          choice is made in Settings → Preferences → Periods, and this says what bounds the rundown and links there. */}
      <Rundown
        slot="bar"
        date={span}
        segments={segments}
        className="shrink-0 print:hidden"
        control={<RundownPeriodSummary onChange={onChangePeriods} />}
        place={room.name}
        rail={railControl}
      />

      {/* `min-h-0` is what makes the two regions below scroll instead of the page: a flex item
          defaults to `min-height: auto`, so the row would otherwise grow to fit the taller of
          them and push the header off the top of the screen. `relative` holds the overlay rail (below lg) and its
          backdrop to this row, under the header and the bar, over the content. */}
      <div className="relative flex min-h-0 flex-1 items-stretch print:block">
        <Rundown
          slot="rail"
          date={span}
          segments={segments}
          className="overflow-y-auto print:hidden"
          control={<RundownPeriodSummary onChange={onChangePeriods} />}
          place={room.name}
          rail={railControl}
        />
        {rail.overlayOpen ? (
          <div
            data-rundown-backdrop
            aria-hidden="true"
            onClick={() => setRail((s) => ({ ...s, overlayOpen: false }))}
            className="absolute inset-0 z-20 bg-black/40 lg:hidden print:hidden"
          />
        ) : null}
        <main ref={mainRef} className="relative min-w-0 flex-1 overflow-y-auto print:overflow-visible">
          <MainScrollProvider value={scrollMainToTop}>
            <MainRevealProvider value={revealInMain}>{children}</MainRevealProvider>
          </MainScrollProvider>
        </main>
      </div>
    </div>
  );
}
